(() => {
  const rootSelector = ".pixqbot-product-layout[data-product-id]";

  class PixqbotProductLayout {
    constructor(root) {
      this.root = root;
      this.productId = root.dataset.productId;
      this.quantityInput = root.querySelector(".m-product-form--main input[name='quantity']");
      this.variantInput = root.querySelector(".m-product-form--main input[name='id']");
      this.packages = root.querySelector("[data-pixqbot-packages]");
      this.packageNote = root.querySelector("[data-pixqbot-package-note]");
      this.packageButtons = Array.from(root.querySelectorAll("[data-pixqbot-package]"));
      this.requiresQikify = this.packages?.dataset.qikifyRequired === "true";
      this.qikifyOffer = null;
      this.qikifyTiers = new Map();
      this.qikifyInputs = [];
      this.variants = this.readVariants();
      this.currentVariant = this.findVariant(this.variantInput?.value) || this.variants.find((variant) => variant.available) || this.variants[0];

      this.handlePackageClick = this.handlePackageClick.bind(this);
      this.handleQuantityChange = this.handleQuantityChange.bind(this);
      this.handleQikifyTierChange = this.handleQikifyTierChange.bind(this);

      this.packageButtons.forEach((button) => button.addEventListener("click", this.handlePackageClick));
      this.quantityInput?.addEventListener("input", this.handleQuantityChange);
      this.quantityInput?.addEventListener("change", this.handleQuantityChange);

      if (window.MinimogEvents?.subscribe) {
        this.unsubscribeVariant = window.MinimogEvents.subscribe(
          `${this.productId}__VARIANT_CHANGE`,
          (variant) => {
            this.currentVariant = this.findVariant(variant?.id) || variant;
            this.render();
            this.startQikifyConnectionPoll();
          }
        );
      }

      this.render();
      this.startQikifyConnectionPoll();
    }

    readVariants() {
      try {
        return JSON.parse(this.root.querySelector("[data-pixqbot-variants]")?.textContent || "[]");
      } catch (error) {
        console.warn("PIXQBOT product layout: invalid variant data.", error);
        return [];
      }
    }

    findVariant(id) {
      return this.variants.find((variant) => String(variant.id) === String(id));
    }

    handlePackageClick(event) {
      const quantity = Number(event.currentTarget.dataset.quantity);
      if (!this.quantityInput || !Number.isFinite(quantity)) return;

      const minimum = Number(this.quantityInput.min) || 1;
      const maximum = this.quantityInput.max ? Number(this.quantityInput.max) : Infinity;
      const step = Number(this.quantityInput.step) || 1;
      const steppedQuantity = minimum + Math.ceil((quantity - minimum) / step) * step;
      const nextQuantity = Math.min(Math.max(steppedQuantity, minimum), maximum);

      if (this.requiresQikify && nextQuantity > 1) {
        if (!this.selectQikifyTier(nextQuantity)) return;
        this.renderPackages();
        return;
      }

      this.quantityInput.value = nextQuantity;
      this.quantityInput.dispatchEvent(new Event("input", { bubbles: true }));
      this.quantityInput.dispatchEvent(new Event("change", { bubbles: true }));
    }

    handleQuantityChange() {
      this.renderPackages();
    }

    handleQikifyTierChange() {
      window.setTimeout(() => this.renderPackages(), 0);
    }

    startQikifyConnectionPoll() {
      if (!this.requiresQikify) return;

      window.clearInterval(this.qikifyPoll);
      let attempts = 0;
      let stablePasses = 0;
      let previousSignature = "";

      const attemptConnection = () => {
        attempts += 1;
        const signature = this.connectQikifyOffer();

        if (signature && signature === previousSignature) {
          stablePasses += 1;
        } else {
          stablePasses = 0;
          previousSignature = signature;
        }

        if ((signature && stablePasses >= 8) || attempts >= 80) {
          window.clearInterval(this.qikifyPoll);
          this.qikifyPoll = null;
        }
      };

      attemptConnection();
      this.qikifyPoll = window.setInterval(attemptConnection, 250);
    }

    connectQikifyOffer() {
      const expectedDiscounts = new Map(
        this.packageButtons.map((button) => [
          Number(button.dataset.quantity),
          Number(button.dataset.expectedDiscount || 0),
        ])
      );
      const offers = Array.from(this.root.querySelectorAll("[data-offer-id]"));
      let matchedOffer = null;
      let matchedTiers = null;

      for (const offer of offers) {
        const tiers = new Map();

        offer.querySelectorAll(".qvl-volume-tier").forEach((tier) => {
          const title = tier.querySelector(".qvl-volume-tier__title")?.textContent || "";
          const quantity = Number(title.match(/\d+/)?.[0]);
          const radio = tier.querySelector("input[type='radio']");
          const discountText = [
            tier.querySelector(".qvl-volume-tier__badge")?.textContent,
            tier.querySelector(".qvl-volume-tier__subtitle")?.textContent,
          ].filter(Boolean).join(" ");
          const discount = Number(discountText.match(/(\d+)\s*%/)?.[1] || 0);
          const unitPrice = this.parseMoney(
            tier.querySelector(".qvl-volume-tier__price--offer-per-unit")?.textContent || ""
          );

          if (!expectedDiscounts.has(quantity) || !radio || unitPrice == null) return;
          if (expectedDiscounts.get(quantity) !== discount) return;

          tiers.set(quantity, { discount, quantity, radio, unitPrice });
        });

        if (Array.from(expectedDiscounts.keys()).every((quantity) => tiers.has(quantity))) {
          matchedOffer = offer;
          matchedTiers = tiers;
          break;
        }
      }

      if (!matchedOffer || !matchedTiers) return "";

      const nextInputs = Array.from(matchedTiers.values()).map((tier) => tier.radio);
      const inputsChanged = nextInputs.some((input, index) => input !== this.qikifyInputs[index]);

      if (inputsChanged) {
        this.qikifyInputs.forEach((input) => input.removeEventListener("change", this.handleQikifyTierChange));
        nextInputs.forEach((input) => {
          input.tabIndex = -1;
          input.addEventListener("change", this.handleQikifyTierChange);
        });
        this.qikifyInputs = nextInputs;
      }

      this.qikifyOffer = matchedOffer;
      this.qikifyTiers = matchedTiers;
      const qikifyEngine = matchedOffer.closest("#qvl-embed-placeholder") || matchedOffer;
      qikifyEngine.classList.add("pixqbot-qikify-engine");
      qikifyEngine.setAttribute("aria-hidden", "true");
      this.packages?.classList.remove("is-awaiting-qikify");
      this.packages?.classList.add("is-qikify-connected");

      if (this.packageNote) {
        this.packageNote.textContent = this.packageNote.dataset.connectedText;
      }

      const currentQuantity = Math.max(Number(this.quantityInput?.value) || 1, 1);
      const currentTier = matchedTiers.get(currentQuantity);
      if (currentTier && !currentTier.radio.checked) currentTier.radio.click();

      this.renderPackages();

      return Array.from(matchedTiers.values())
        .map((tier) => `${tier.quantity}:${tier.discount}:${tier.unitPrice}`)
        .join("|");
    }

    selectQikifyTier(quantity) {
      const tier = this.qikifyTiers.get(quantity);
      if (!tier?.radio) return false;

      tier.radio.click();
      return tier.radio.checked;
    }

    parseMoney(value) {
      const compactValue = String(value || "").replace(/\s/g, "");
      const decimalMatch = compactValue.match(/([,.])(\d{2})(?!\d)/);
      const decimalDigits = decimalMatch?.[2] || "00";
      const integerSource = decimalMatch
        ? compactValue.slice(0, decimalMatch.index)
        : compactValue;
      const integerDigits = integerSource.replace(/\D/g, "");

      if (!integerDigits) return null;
      return Number(integerDigits) * 100 + Number(decimalDigits);
    }

    render() {
      if (!this.currentVariant) return;
      this.renderAvailability();
      this.renderVariantFacts();
      this.renderPackages();
    }

    renderAvailability() {
      const available = Boolean(this.currentVariant.available);
      const availability = this.root.querySelector("[data-pixqbot-availability]");
      const availabilityLabel = this.root.querySelector("[data-pixqbot-availability-label]");
      const deliveryLabel = this.root.querySelector("[data-pixqbot-delivery-label]");
      const specificationAvailability = this.root.querySelector("[data-pixqbot-spec-availability]");

      availability?.classList.toggle("is-unavailable", !available);
      if (availabilityLabel) availabilityLabel.textContent = available ? "În stoc" : "Stoc epuizat";
      if (specificationAvailability) specificationAvailability.textContent = available ? "În stoc" : "Stoc epuizat";
      if (deliveryLabel && availability) {
        deliveryLabel.textContent = available
          ? availability.dataset.deliveryText
          : "Indisponibil momentan";
      }
    }

    renderVariantFacts() {
      this.root.querySelectorAll("[data-pixqbot-sku]").forEach((element) => {
        element.textContent = this.currentVariant.sku || element.dataset.fallbackValue || "";
      });

      const options = Array.isArray(this.currentVariant.options) ? this.currentVariant.options : [];
      this.root.querySelectorAll("[data-pixqbot-option-value]").forEach((element) => {
        const optionIndex = Number(element.dataset.pixqbotOptionValue);
        if (options[optionIndex] != null) element.textContent = options[optionIndex];
      });
    }

    renderPackages() {
      const variant = this.currentVariant;
      if (!variant) return;

      const currentQuantity = Math.max(Number(this.quantityInput?.value) || 1, 1);
      const maximum = this.quantityInput?.max ? Number(this.quantityInput.max) : Infinity;
      const managedInventory = Boolean(variant.inventory_management);
      const denyOverselling = variant.inventory_policy !== "continue";
      const inventoryQuantity = Number(variant.inventory_quantity);

      this.packageButtons.forEach((button) => {
        const quantity = Number(button.dataset.quantity) || 1;
        const exceedsInventory = managedInventory && denyOverselling && Number.isFinite(inventoryQuantity) && inventoryQuantity < quantity;
        const qikifyTier = this.qikifyTiers.get(quantity);
        const awaitingRequiredDiscount = this.requiresQikify && quantity > 1 && !qikifyTier;
        const disabled = !variant.available || maximum < quantity || exceedsInventory || awaitingRequiredDiscount;
        const selected = currentQuantity === quantity;

        button.disabled = disabled;
        button.classList.toggle("is-selected", selected);
        button.setAttribute("aria-pressed", String(selected));

        const total = button.querySelector("[data-pixqbot-package-total]");
        const unit = button.querySelector("[data-pixqbot-package-unit]");
        const discount = button.querySelector("[data-pixqbot-package-discount]");
        const unitPrice = qikifyTier?.unitPrice ?? Number(variant.price || 0);

        if (total) total.textContent = this.formatMoney(unitPrice * quantity);
        if (unit) unit.textContent = this.formatMoney(unitPrice);

        if (discount) {
          discount.hidden = !qikifyTier || qikifyTier.discount <= 0;
          if (!discount.hidden) discount.textContent = `-${qikifyTier.discount}%`;
        }

        if (qikifyTier?.discount > 0) {
          button.setAttribute(
            "aria-label",
            `${quantity} ${quantity === 1 ? "pachet" : "pachete"}, reducere ${qikifyTier.discount}%, total ${this.formatMoney(unitPrice * quantity)}`
          );
        } else {
          button.removeAttribute("aria-label");
        }
      });
    }

    formatMoney(cents) {
      if (typeof window.formatMoney === "function") {
        return window.formatMoney(Math.round(cents), window.MinimogSettings?.money_format);
      }

      return new Intl.NumberFormat("ro-RO", {
        style: "currency",
        currency: "RON",
      }).format(Math.round(cents) / 100);
    }

    destroy() {
      this.packageButtons.forEach((button) => button.removeEventListener("click", this.handlePackageClick));
      this.quantityInput?.removeEventListener("input", this.handleQuantityChange);
      this.quantityInput?.removeEventListener("change", this.handleQuantityChange);
      this.qikifyInputs.forEach((input) => input.removeEventListener("change", this.handleQikifyTierChange));
      window.clearInterval(this.qikifyPoll);
      this.unsubscribeVariant?.();
      delete this.root.dataset.pixqbotInitialized;
    }
  }

  const initialize = (scope = document) => {
    const roots = scope.matches?.(rootSelector)
      ? [scope]
      : Array.from(scope.querySelectorAll?.(rootSelector) || []);

    roots.forEach((root) => {
      if (root.dataset.pixqbotInitialized === "true") return;
      root.dataset.pixqbotInitialized = "true";
      root.pixqbotProductLayout = new PixqbotProductLayout(root);
    });
  };

  initialize();

  document.addEventListener("shopify:section:load", (event) => initialize(event.target));
  document.addEventListener("shopify:section:unload", (event) => {
    const root = event.target.matches?.(rootSelector)
      ? event.target
      : event.target.querySelector?.(rootSelector);
    root?.pixqbotProductLayout?.destroy();
  });
})();
